import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Package, Tags, Users, MessageSquare, IndianRupee, Settings, LogOut, Menu, X,
  Inbox, UserSearch, ClipboardList, Target, Handshake, Receipt, TrendingUp, BarChart3, Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  { title: null, links: [{ to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard }] },
  { title: "CRM", links: [
    { to: "/admin/buyers", label: "Buyers", icon: UserSearch },
    { to: "/admin/requirements", label: "Requirements", icon: ClipboardList },
    { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
    { to: "/admin/crm-leads", label: "Leads", icon: Target },
    { to: "/admin/deals", label: "Deals", icon: Handshake },
  ] },
  { title: "Inventory", links: [
    { to: "/admin/machines", label: "Listings", icon: Package },
    { to: "/admin/sellers", label: "Sellers", icon: Users },
    { to: "/admin/categories", label: "Categories", icon: Tags },
    { to: "/admin/leads", label: "Sell Leads", icon: Inbox },
  ] },
  { title: "Sales", links: [
    { to: "/admin/service-charges", label: "Service Charges", icon: Receipt },
    { to: "/admin/revenue", label: "Revenue", icon: TrendingUp },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/fees", label: "Listing Fees", icon: IndianRupee },
  ] },
  { title: null, links: [{ to: "/admin/settings", label: "Settings", icon: Settings }] },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [gq, setGq] = useState("");

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50">Loading…</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const doLogout = async () => { await logout(); navigate("/admin"); };
  const doSearch = (e) => { e.preventDefault(); if (gq.trim().length >= 2) { navigate(`/admin/search?q=${encodeURIComponent(gq.trim())}`); setOpen(false); } };

  const SearchBox = () => (
    <form onSubmit={doSearch} className="relative px-3 pb-1 pt-3">
      <Search className="pointer-events-none absolute left-6 top-[1.35rem] h-4 w-4 text-slate-400" />
      <input data-testid="global-search-box" value={gq} onChange={(e) => setGq(e.target.value)} placeholder="Search everything…"
        className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm focus:border-brand focus:bg-white focus:outline-none" />
    </form>
  );

  const Nav = () => (
    <nav className="flex flex-col gap-0.5 p-3">
      <SearchBox />
      {SECTIONS.map((sec, i) => (
        <div key={i} className="mb-1">
          {sec.title && <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{sec.title}</p>}
          {sec.links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <l.icon size={17} /> {l.label}
            </NavLink>
          ))}
        </div>
      ))}
      <button onClick={doLogout} data-testid="admin-logout" className="mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
        <LogOut size={18} /> Logout
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <aside className="fixed left-0 top-0 hidden h-screen w-60 shrink-0 overflow-y-auto border-r border-slate-200 bg-white lg:block">
          <div className="border-b border-slate-200 p-5">
            <span className="font-display text-xl font-extrabold text-slate-900">MASHIN<span className="text-orange">IYA</span></span>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Admin CRM</p>
          </div>
          <Nav />
        </aside>

        <div className="flex-1 lg:ml-60">
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 lg:hidden">
            <span className="font-display font-extrabold">MASHINIYA Admin</span>
            <button onClick={() => setOpen(true)}><Menu size={22} /></button>
          </header>
          {open && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
              <div className="absolute left-0 top-0 h-full w-64 overflow-y-auto bg-white">
                <div className="flex items-center justify-between border-b border-slate-200 p-4">
                  <span className="font-display font-extrabold">Menu</span>
                  <button onClick={() => setOpen(false)}><X size={22} /></button>
                </div>
                <Nav />
              </div>
            </div>
          )}
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
